import React from 'react';
import styled from 'styled-components';
import { ApolloProvider } from '@apollo/client';
import { Query } from '@apollo/client/react/components';
import { NullState, PageTitle } from 'bench-internal-theme';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import { SpinnerOverlay } from 'spinner';
import { ToastContainer } from 'react-toastify';
import { QueryClient, QueryClientProvider } from 'react-query';

import BackboneAppContainer from './BackboneAppContainer';
import BkToolsRoutes from './BkToolsRoutes';
import BkToolsSidebar from './BkToolsSidebar';
import ClientIdContext from './ClientIdContext';
import ClientProvider from './ClientContext';
import Error404 from './Error404';
import GET_CURRENT_PERSON from './queries';
import graphqlClient from './graphqlClient';
import PersonIdContext from './PersonIdContext';
import SidebarDismissBypassingCloseButton from './SidebarDismissBypassingCloseButton';
import BKToolsErrorBoundary from './BKToolsErrorBoundary';
import { FeatureFlagProvider } from '@bench/ff-client';
import { SplitFeatureFlags } from './features';
import { TaskManagerApp } from 'task-manager';
import { Box, Typography } from '@material-ui/core';
import {
  initializeDataDogRUM,
  setDataDogRUMUser,
  setDataDogRUMContext,
  addFeatureFlagEvaluation,
} from './utils/datadogRUMSetup';
import { setUser as setUsageTrackerUser } from './utils/usageTracker';

const AppNavigationSidebar = styled.div`
  bottom: 0;
  left: 0;
  position: fixed;
  top: 0;
  width: 220px;
  z-index: 2;
`;

const AppMainContent = styled.div`
  margin-left: 220px;
  min-height: 100vh;
  // By using relative positioning for the main content, the user can still scroll the main content
  // area while their cursor is over the fixed position sidebar, and the main content still has a
  // correctly sized and positioned containing block.
  position: relative;
`;

function storeOktaOpenId(currentUser) {
  localStorage.setItem('openid_okta', JSON.stringify(currentUser.roles.includes('OPENID_OKTA')));
}

// Create QueryClient instance for react-query at the app level
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
    },
  },
});

export default function BkToolsApp() {
  return (
    <QueryClientProvider client={queryClient}>
      <ApolloProvider client={graphqlClient}>
        <BrowserRouter>
          <Switch>
            <Route
              exact
              path="/app/task-manager"
              render={() => (
                <>
                  <PageTitle title="Task Manager" />
                  <TaskManagerApp />
                </>
              )}
            />
            <Route path="/app/:clientId">
              {({ match: clientPrefixMatch, history }) => {
                if (!clientPrefixMatch) {
                  return <Error404 />;
                }
                const {
                  path: patternPrefix,
                  url: pathPrefix,
                  params: { clientId },
                } = clientPrefixMatch;
                if (!clientId) {
                  return <Error404 />;
                }
                return (
                  <BKToolsErrorBoundary clientId={clientId}>
                    <ClientIdContext.Provider value={clientId}>
                      <Query query={GET_CURRENT_PERSON}>
                        {getCurrentPerson => (
                          <BackboneAppContainer clientId={clientId} history={history}>
                            {({ isLoading, client, error }) => {
                              if (window.heap) {
                                window.heap.track('BkTools mount');
                              }

                              if (window.BENCH.vendor.datadog?.bk) {
                                initializeDataDogRUM(
                                  window.BENCH.vendor.datadog.bk,
                                  window.BENCH.env,
                                );
                              }

                              if (isLoading || getCurrentPerson.loading) {
                                document.title = 'Loading BK Tools';
                                return <SpinnerOverlay active />;
                              }

                              if (error) {
                                return (
                                  <Box>
                                    There was an error loading BK Tools.
                                    <Typography variant="caption">Details: {error}</Typography>
                                  </Box>
                                );
                              }

                              if (
                                getCurrentPerson.data &&
                                getCurrentPerson.data.currentUser &&
                                getCurrentPerson.data.currentUser.roles
                              ) {
                                storeOktaOpenId(getCurrentPerson.data.currentUser);
                              }

                              const currentPersonId =
                                getCurrentPerson.data &&
                                getCurrentPerson.data.currentUser &&
                                getCurrentPerson.data.currentUser.id;

                              if (!currentPersonId || getCurrentPerson.error) {
                                return (
                                  <NullState
                                    bigEmoji="👽"
                                    description="New phone, who dis?"
                                    title="It seems like you are logged out so we are attempting to log you back in through Okta."
                                  />
                                );
                              }

                              setDataDogRUMUser(currentPersonId);
                              setUsageTrackerUser(currentPersonId);
                              setDataDogRUMContext('clientId', clientId);

                              if (typeof window.AppcuesReady === 'function') {
                                window.AppcuesReady(() => {
                                  window.Appcues?.identify(currentPersonId, { clientId });
                                  window.Appcues?.group(clientId);
                                });
                              }

                              const ffConfig = {
                                core: {
                                  authorizationKey: window.BENCH
                                    ? window.BENCH.vendor.split.authorizationKey
                                    : '',
                                  key: clientId,
                                },
                                impressionListener: {
                                  logImpression: impressionData => {
                                    addFeatureFlagEvaluation(
                                      impressionData.impression.feature,
                                      impressionData.impression.treatment,
                                    );
                                  },
                                },
                              };

                              const clientAttributes = {
                                id: client?.id || '',
                                email: client?.email || '',
                                company: client?.company || '',
                                subscriptionPlan: client?.subscriptionPlan || '',
                                inboxState: client?.inboxState || '',
                                roles: client?.roles || [],
                                isB4AClient: client?.isB4AClient || false,
                                isMultiBusinessPerson: client?.isMultiBusinessPerson || false,
                                bookkeeperId:
                                  client?.assignedPersons?.bookkeepers?.primary?.id || '',
                                bookkeeperEmail:
                                  client?.assignedPersons?.bookkeepers?.primary?.email || '',
                                salespersonId:
                                  client?.assignedPersons?.salespersons?.primary?.id || '',
                                salespersonEmail:
                                  client?.assignedPersons?.salespersons?.primary?.email || '',
                                clientSuccessId:
                                  client?.assignedPersons?.clientSuccess?.primary?.id || '',
                                clientSuccessEmail:
                                  client?.assignedPersons?.clientSuccess?.primary?.email || '',
                              };

                              return (
                                <PersonIdContext.Provider value={currentPersonId}>
                                  <ClientProvider client={client}>
                                    <FeatureFlagProvider
                                      config={ffConfig}
                                      clientAttributes={clientAttributes}
                                      features={Object.values(SplitFeatureFlags)}
                                    >
                                      <AppNavigationSidebar>
                                        <BkToolsSidebar
                                          clientId={clientId}
                                          client={client}
                                          pathPrefix={pathPrefix}
                                          patternPrefix={patternPrefix}
                                        />
                                      </AppNavigationSidebar>

                                      <AppMainContent>
                                        <BkToolsRoutes
                                          clientId={clientId}
                                          patternPrefix={patternPrefix}
                                        />
                                      </AppMainContent>

                                      <ToastContainer
                                        autoClose={false}
                                        closeButton={<SidebarDismissBypassingCloseButton />}
                                        hideProgressBar
                                        position="bottom-right"
                                      />
                                    </FeatureFlagProvider>
                                  </ClientProvider>
                                </PersonIdContext.Provider>
                              );
                            }}
                          </BackboneAppContainer>
                        )}
                      </Query>
                    </ClientIdContext.Provider>
                  </BKToolsErrorBoundary>
                );
              }}
            </Route>
          </Switch>
        </BrowserRouter>
      </ApolloProvider>
    </QueryClientProvider>
  );
}
